import React, { FunctionComponent, useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { RiSidebarFoldLine, RiSidebarUnfoldLine } from "react-icons/ri"
import { MenuItem, useMenuItems, useMenuGroups } from "../hooks/useMenuItems"
import { useTypedSelector, useTypedDispatch, RootState } from "../../../app/store"
import { RoleName, Role } from "../../auth/types"
import { setRole } from "../../auth/authSlice"
import { useRoleRedirect } from "../../auth/hooks/useRoleRedirect"
import { updateSidebar } from "../sidebarSlice"

// Icon wrapper components
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SidebarFoldIcon = (): JSX.Element => <RiSidebarFoldLine size={20} color="#9ca3af" />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SidebarUnfoldIcon = (): JSX.Element => <RiSidebarUnfoldLine size={20} color="#9ca3af" />

type SidebarContentsProps = {
  isOpen: boolean
}

export const SIDEBAR_WIDTH = 260
export const SIDEBAR_WIDTH_CLOSED = 72

// Meseekna brand colors
const MESEEKNA_BLUE = "#4d86ff"

const SidebarContents: FunctionComponent<SidebarContentsProps> = ({
  isOpen,
}) => {
  const navigate = useNavigate()
  const dispatch = useTypedDispatch()
  const menuItems = useMenuItems()
  const menuGroups = useMenuGroups()
  const { handleRoleRedirectForRole } = useRoleRedirect()

  const user = useTypedSelector((state: RootState) => state.authSlice.user)
  const currentRole = useTypedSelector((state: RootState) => state.authSlice.role)
  const isAdmin = user?.roles?.some((role) => role.roleId === RoleName.Admin) || user?.role === RoleName.Admin
  const isSiteAdmin = user?.roles?.some((role) => role.roleId === RoleName.SiteAdmin) || user?.role === RoleName.SiteAdmin
  const isRecruiter = user?.roles?.some((role) => role.roleId === RoleName.Recruiter) || user?.role === RoleName.Recruiter
  const isTeamMember = user?.roles?.some((role) => role.roleId === RoleName.TeamMember) || user?.role === RoleName.TeamMember

  // Dev-only role switcher state
  const [isRoleSwitcherOpen, setIsRoleSwitcherOpen] = useState(false)

  const toggleSidebar = () => {
    dispatch(updateSidebar(!isOpen))
  }

  const handleRoleChange = (role: Role) => {
    dispatch(setRole(role))
    handleRoleRedirectForRole(role)
    setIsRoleSwitcherOpen(false)
  }

  // Filter items for non-admin users (no groups)
  const nonGroupedItems = menuItems.filter((item) => !item.group)

  // Use grouped navigation for Admin, SiteAdmin, Recruiter, and TeamMember
  const useGroupedNav = (isAdmin || isSiteAdmin || isRecruiter || isTeamMember) && menuGroups.length > 0

  const navigateTo = (item: MenuItem) => {
    if (navigate) {
      navigate(item.path, {
        state: { group: { id: item.id, name: item.name } },
      })
    }
  }

  const onHeaderContainerClick = useCallback(() => {
    if (isSiteAdmin) {
      navigate("/dashboard/super-admin")
    } else if (isAdmin) {
      navigate("/dashboard/recruiter-admin")
    } else if (isRecruiter) {
      navigate("/dashboard/recruiter-admin")
    } else if (isTeamMember) {
      navigate("/dashboard/simulations")
    } else {
      navigate("/dashboard")
    }
  }, [navigate, isAdmin, isSiteAdmin, isRecruiter, isTeamMember])

  const renderIcon = (icon: { icon: boolean; image: string | (() => React.ReactElement) }) => {
    if (!icon.icon) {
      return (
        <img
          className="w-5 h-5 overflow-hidden shrink-0 object-contain"
          alt=""
          src={icon.image as string}
        />
      )
    } else {
      const IconComponent = icon.image as () => React.ReactElement
      return IconComponent()
    }
  }

  const renderMenuItem = (item: MenuItem) => (
    <button
      key={item.id}
      disabled={item.disable}
      onClick={() => navigateTo(item)}
      className={`w-full rounded-md overflow-hidden flex flex-row items-center px-3 py-2 transition-colors ${
        item.active
          ? "bg-blue-50"
          : "bg-gray-100 hover:bg-gray-200"
      }`}
      style={{
        opacity: item.disable ? 0.5 : 1,
        cursor: item.disable ? "not-allowed" : "pointer",
      }}>
      <div
        className={`flex flex-row items-center h-6 ${isOpen ? "justify-start gap-3 flex-1" : "justify-center w-full"}`}>
        <span style={{ color: item.active ? MESEEKNA_BLUE : "#1f2937" }}>
          {renderIcon(item[isOpen ? "openIcon" : "closeIcon"])}
        </span>

        {isOpen && (
          <span
            data-testid={item.name}
            className="text-sm font-medium truncate"
            style={{ color: item.active ? MESEEKNA_BLUE : "#1f2937" }}>
            {item.name}
          </span>
        )}
      </div>
    </button>
  )

  return (
    <div
      className="bg-white border-r border-gray-200 h-full flex flex-col"
      style={{ width: isOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED }}
    >
      {/* Logo Header */}
      <div
        className="flex items-center px-4 py-5 cursor-pointer border-b border-gray-100"
        onClick={onHeaderContainerClick}>
        <div className={`flex items-center ${isOpen ? "justify-start" : "justify-center w-full"}`}>
          <img
            className={`${isOpen ? "h-8" : "h-8 w-8"} object-contain`}
            alt="meseekna-logo"
            src={
              isOpen
                ? "/v3/assets/meseekna-logo.png"
                : "/v3/assets/image-14@2x.png"
            }
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {useGroupedNav ? (
          // Admin/SiteAdmin grouped navigation
          <div className="space-y-6">
            {menuGroups.map((group) => (
              <div key={group.id}>
                {/* Group Label */}
                {group.label && isOpen && (
                  <div className="px-3 mb-2">
                    <span
                      className="text-xs font-semibold tracking-wider"
                      style={{ color: MESEEKNA_BLUE }}
                    >
                      {group.label}
                    </span>
                  </div>
                )}
                {/* Group Items */}
                <div className="space-y-1">
                  {group.items.map(renderMenuItem)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Non-admin flat navigation
          <div className="space-y-1">
            {nonGroupedItems.map(renderMenuItem)}
          </div>
        )}

        {/* Toggle Sidebar Button - at the end of scrollable content */}
        <div className="mt-8 pt-4 border-t border-gray-100">
          <button
            onClick={toggleSidebar}
            className={`w-full rounded-md overflow-hidden flex flex-row items-center px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors ${isOpen ? "justify-start gap-3" : "justify-center"}`}
          >
            {isOpen ? <SidebarFoldIcon /> : <SidebarUnfoldIcon />}
            {isOpen && (
              <span className="text-sm text-gray-500">Collapse sidebar</span>
            )}
          </button>
        </div>
      </nav>

      {/* Footer area - Dev role switcher only */}
      {process.env.NODE_ENV === "development" && user?.roles?.length > 1 && (
        <div className="border-t border-gray-100 px-3 py-3 pb-12">
          <div className="relative">
            <button
              onClick={() => setIsRoleSwitcherOpen(!isRoleSwitcherOpen)}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              title="Switch Role (Dev Only)"
            >
              {isOpen ? "Switch Role (Dev)" : "Dev"}
            </button>

            {isRoleSwitcherOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsRoleSwitcherOpen(false)}
                />
                <div className={`absolute ${isOpen ? "bottom-full left-0 mb-2" : "bottom-full left-full ml-2 mb-0"} w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50`}>
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs text-gray-500">Switch Role (Dev):</p>
                  </div>
                  {user.roles.map((role) => (
                    <button
                      key={role.roleId}
                      onClick={() => handleRoleChange(role)}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${
                        role.roleId === currentRole?.roleId ? "text-[#4d86ff] font-medium" : "text-gray-700"
                      }`}
                    >
                      {role.name || (role.roleId === 4 ? "Team Admin" : role.roleId === 5 ? "Simulation User" : role.roleId === 8 ? "Admin" : "Unknown")}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SidebarContents
